import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { formatCr, formatDate } from "@/lib/utils-ros";
import { Package, ChevronDown, ChevronRight, CheckCircle2, Circle, IndianRupee, Wallet, CalendarClock, Filter, X } from "lucide-react";
import { MeetingLogInline } from "@/components/revenueos/MeetingLog";
import { PartnerAssignmentPanel } from "@/components/revenueos/PartnerAssignment";
import { PageToolbar } from "@/components/revenueos/PageToolbar";
import { EditableField } from "@/components/revenueos/EditableField";
import { formatLakh, MONEY_UNIT_SHORT } from "@/lib/utils-ros";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth.jsx";

const ORDER_STATUSES = ["received", "in_production", "shipped", "delivered", "invoiced", "paid", "cancelled"];

const STATUS_COLOR = {
  received: "#3B82F6", in_production: "#F59E0B", shipped: "#8B5CF6",
  delivered: "#10B981", invoiced: "#84CC16", paid: "var(--copper)", cancelled: "#EF4444"
};

const PAYMENT_STATUS_LABEL = {
  awaiting_advance: "Awaiting advance",
  awaiting_balance: "Awaiting balance",
  fully_paid: "Fully paid",
};

const PAYMENT_STATUS_COLOR = {
  awaiting_advance: "#F59E0B",
  awaiting_balance: "#3B82F6",
  fully_paid: "#10B981",
};

function orderLabel(value) {
  return String(value || "").replaceAll("_", " ").replace(/\b\w/g, char => char.toUpperCase());
}

function OrderFilterSelect({ label, value, onChange, options, testId }) {
  return (
    <label className="block">
      <span className="block text-[11px] font-semibold text-[#455A64] mb-1.5">{label}</span>
      <select
        value={value}
        onChange={event => onChange(event.target.value)}
        data-testid={testId}
        className="w-full h-10 rounded-xl border border-[#B8D3DF] bg-[#F8FCFE] px-3 text-[12px] outline-none focus:border-[#00A2D4]"
      >
        {options.map(option => <option key={option.key} value={option.key}>{option.label}</option>)}
      </select>
    </label>
  );
}

function PaymentBadge({ status }) {
  const label = PAYMENT_STATUS_LABEL[status] || "Payment";
  const color = PAYMENT_STATUS_COLOR[status] || "var(--text-muted)";
  return (
    <span
      className="text-[9px] font-semibold px-2.5 py-1 rounded-full border whitespace-nowrap"
      style={{ color, borderColor: `${color}40`, background: `${color}10` }}
    >
      {label}
    </span>
  );
}

function ContractBadge({ order }) {
  if (order.renewal_window_active) {
    return (
      <span className="text-[9px] font-semibold px-2.5 py-1 rounded-full border border-[#F59E0B]/40 bg-[#F59E0B]/10 text-[#B45309] whitespace-nowrap">
        Renewal due
      </span>
    );
  }
  if (order.contract_type === "multi_year" && (order.year_payments_due?.length > 0)) {
    return (
      <span className="text-[9px] font-semibold px-2.5 py-1 rounded-full border border-[#8B5CF6]/40 bg-[#8B5CF6]/10 text-[#6D28D9] whitespace-nowrap">
        Year payment due
      </span>
    );
  }
  if (order.contract_type === "multi_year") {
    return (
      <span className="text-[9px] font-semibold px-2.5 py-1 rounded-full border border-[#C9DCE6] bg-[#F3FAFD] text-[#607D8B] whitespace-nowrap">
        {order.contract_years}-yr plan
      </span>
    );
  }
  return null;
}

function OrderContractPanel({ order, onPatch, readOnly = false }) {
  const [busy, setBusy] = useState(null);
  const [contractYears, setContractYears] = useState(order.contract_years ?? 1);
  const [contractStart, setContractStart] = useState((order.contract_start || "").slice(0, 10));

  useEffect(() => {
    setContractYears(order.contract_years ?? 1);
    setContractStart((order.contract_start || "").slice(0, 10));
  }, [order.id, order.contract_years, order.contract_start]);

  async function save(patch) {
    setBusy(true);
    try {
      const { data } = await api.patch(`/orders/${order.id}`, patch);
      onPatch(order.id, data);
      toast.success("Contract updated");
    } catch {
      toast.error("Contract update failed");
    } finally {
      setBusy(null);
    }
  }

  async function toggleYearPayment(year, received) {
    const payments = (order.year_payments || []).map((p) =>
      p.year === year ? { ...p, received, received_cr: received ? p.expected_cr : null } : p
    );
    await save({ year_payments: payments });
  }

  const isMulti = order.contract_type === "multi_year";

  return (
    <div className="border border-[#C9DCE6] rounded-2xl bg-white p-4 space-y-4 shadow-[0_6px_18px_rgba(7,83,154,0.05)]" data-testid={`order-contract-${order.id}`}>
      <div className="flex items-center gap-2 flex-wrap">
        <CalendarClock size={14} className="text-[#07539A]" />
        <span className="text-[12px] font-semibold text-[#07539A]">Contract terms</span>
        <ContractBadge order={order} />
        {order.renewal_window_active && (
          <span className="text-[10px] text-[var(--text-muted)]">
            Month {order.months_elapsed} · {order.months_until_expiry} mo to expiry
          </span>
        )}
      </div>

      <div className="grid md:grid-cols-3 gap-3">
        <div className="border border-[#D7E8F0] rounded-xl bg-[#F8FCFE] p-3">
          <div className="text-[10px] font-semibold text-[#607D8B] mb-1.5">Engagement</div>
          <select
            value={order.contract_type || "single_year"}
            disabled={readOnly || busy}
            onChange={async (e) => {
              const contract_type = e.target.value;
              const patch = { contract_type };
              if (contract_type === "multi_year") patch.contract_years = order.contract_years > 1 ? order.contract_years : 3;
              await save(patch);
            }}
            data-testid={`order-contract-type-${order.id}`}
            className="w-full h-9 bg-white border border-[#B8D3DF] rounded-lg px-2.5 text-[13px] outline-none focus:border-[#00A2D4]"
          >
            <option value="single_year">Single-year</option>
            <option value="multi_year">Multi-year</option>
          </select>
        </div>
        <div className="border border-[#D7E8F0] rounded-xl bg-[#F8FCFE] p-3">
          <div className="text-[10px] font-semibold text-[#607D8B] mb-1.5">Years</div>
          <input
            type="number"
            min={1}
            max={10}
            disabled={readOnly || !isMulti || busy}
            value={contractYears}
            onChange={(e) => setContractYears(parseInt(e.target.value, 10) || 1)}
            onBlur={async (e) => {
              const v = parseInt(e.target.value, 10);
              if (Number.isNaN(v) || v === order.contract_years) return;
              await save({ contract_years: v, contract_type: v > 1 ? "multi_year" : "single_year" });
            }}
            data-testid={`order-contract-years-${order.id}`}
            className="w-full h-9 bg-white border border-[#B8D3DF] rounded-lg px-2.5 text-[13px] outline-none focus:border-[#00A2D4] disabled:opacity-50"
          />
        </div>
        <div className="border border-[#D7E8F0] rounded-xl bg-[#F8FCFE] p-3">
          <div className="text-[10px] font-semibold text-[#607D8B] mb-1.5">Start date</div>
          <input
            type="date"
            value={contractStart}
            disabled={readOnly || busy}
            onChange={(e) => setContractStart(e.target.value)}
            onBlur={async (e) => {
              const v = e.target.value;
              if (!v || v === (order.contract_start || "").slice(0, 10)) return;
              await save({ contract_start: v });
            }}
            data-testid={`order-contract-start-${order.id}`}
            className="w-full h-9 bg-white border border-[#B8D3DF] rounded-lg px-2.5 text-[13px] outline-none focus:border-[#00A2D4]"
          />
        </div>
      </div>

      {isMulti && (
        <div className="space-y-2">
          <div className="text-[10px] uppercase tracking-widest text-[var(--text-dim)]">
            Annual payments · {formatCr(order.annual_value_cr)} / year · ends {order.contract_end}
          </div>
          <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2">
            {(order.year_payments || []).map((yp) => (
              <div
                key={yp.year}
                className={`border rounded-md p-2.5 ${yp.received ? "border-[#10B981]/40 bg-[#10B981]/5" : yp.year >= 2 && !yp.received && (order.year_payments_due || []).some((d) => d.year === yp.year) ? "border-[#8B5CF6]/40" : "border-black/10"}`}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[11px] font-medium">Year {yp.year}</span>
                  {yp.year >= 2 && !readOnly && (
                    <button
                      type="button"
                      disabled={readOnly || busy}
                      onClick={() => toggleYearPayment(yp.year, !yp.received)}
                      data-testid={`order-year-${yp.year}-${order.id}`}
                      className="flex items-center gap-1 text-[10px] text-[var(--text-muted)] hover:text-[var(--saarv-blue)]"
                    >
                      {yp.received ? <CheckCircle2 size={12} className="text-[#10B981]" /> : <Circle size={12} />}
                      {yp.received ? "Received" : "Mark received"}
                    </button>
                  )}
                </div>
                <div className="text-[11px] font-mono tabular-nums text-[var(--text-muted)] mt-1">{formatCr(yp.expected_cr)}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!isMulti && order.renewal_window_active && (
        <div className="text-[12px] text-[var(--copper-strong)] bg-[var(--copper)]/5 border border-[var(--copper)]/20 rounded-md p-2.5">
          Renewal window open — partner and customer outreach prompts are active from month 8 until contract expiry ({order.contract_end}).
        </div>
      )}
    </div>
  );
}

function OrderPaymentPanel({ order, onPatch, readOnly = false }) {
  const [busy, setBusy] = useState(null);

  async function savePayment(patch) {
    setBusy(Object.keys(patch)[0]);
    try {
      const { data } = await api.patch(`/orders/${order.id}`, patch);
      onPatch(order.id, data);
      toast.success("Payment updated");
    } catch {
      toast.error("Payment update failed");
    } finally {
      setBusy(null);
    }
  }

  const advanceExpected = order.advance_expected_cr ?? 0;
  const balanceExpected = order.balance_expected_cr ?? 0;

  return (
    <div className="border border-[#C9DCE6] rounded-[24px] bg-white p-4 space-y-4 shadow-[0_6px_18px_rgba(7,83,154,0.05)]" data-testid={`order-payment-${order.id}`}>
      <div className="flex items-center gap-2">
        <IndianRupee size={14} className="text-[#07539A]" />
        <span className="text-[12px] font-semibold text-[#07539A]">Payment &amp; PO terms</span>
        <PaymentBadge status={order.payment_status} />
      </div>

      <EditableField
        label="PO payment terms"
        value={order.payment_terms || ""}
        testId={`order-payment-terms-${order.id}`}
        multiline
        readOnly={readOnly}
        className="!rounded-2xl !border-[#D7E8F0] bg-[#F8FCFE] !p-3"
        labelClassName="text-[11px] font-semibold text-[#455A64]"
        inputClassName="text-[#243B53]"
        placeholder="e.g. 30% advance on PO, balance Net 30 from delivery"
        onSave={async (v) => {
          await savePayment({ payment_terms: v.trim() });
        }}
      />

      <div className="grid md:grid-cols-2 gap-3">
        <div className="border border-[#D7E8F0] rounded-2xl bg-[#F8FCFE] p-3">
          <div className="text-[11px] font-semibold text-[#455A64] mb-1.5">Advance %</div>
          <input
            type="number"
            min={0}
            max={100}
            step={1}
            defaultValue={order.advance_pct ?? 30}
            disabled={readOnly}
            onBlur={async (e) => {
              const v = parseFloat(e.target.value);
              if (Number.isNaN(v) || v === order.advance_pct) return;
              await savePayment({ advance_pct: v });
            }}
            data-testid={`order-advance-pct-${order.id}`}
            className="w-full h-9 bg-white border border-[#B8D3DF] rounded-xl px-3 text-[13px] text-[#243B53] outline-none focus:border-[#00A2D4]"
          />
        </div>
        <div className="border border-[#D7E8F0] rounded-2xl bg-[#F8FCFE] p-3">
          <div className="text-[11px] font-semibold text-[#455A64] mb-1.5">Balance due (days)</div>
          <input
            type="number"
            min={0}
            step={1}
            defaultValue={order.balance_due_days ?? 30}
            disabled={readOnly}
            onBlur={async (e) => {
              const v = parseInt(e.target.value, 10);
              if (Number.isNaN(v) || v === order.balance_due_days) return;
              await savePayment({ balance_due_days: v });
            }}
            data-testid={`order-balance-days-${order.id}`}
            className="w-full h-9 bg-white border border-[#B8D3DF] rounded-xl px-3 text-[13px] text-[#243B53] outline-none focus:border-[#00A2D4]"
          />
        </div>
      </div>

      <div className="grid md:grid-cols-2 gap-3">
        {/* Advance */}
        <div className={`border rounded-2xl p-3.5 ${order.advance_received ? "border-[#10B981]/40 bg-[#10B981]/5" : "border-[#D7E8F0] bg-[#F8FCFE]"}`}>
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="text-[11px] font-semibold text-[#455A64]">Advance</div>
            {!readOnly && (
            <button
              type="button"
              disabled={busy === "advance_received"}
              onClick={() => savePayment({ advance_received: !order.advance_received })}
              data-testid={`order-advance-toggle-${order.id}`}
              className="flex items-center gap-1.5 text-[11px] text-[var(--text-muted)] hover:text-[var(--saarv-blue)] disabled:opacity-50"
            >
              {order.advance_received ? <CheckCircle2 size={14} className="text-[#10B981]" /> : <Circle size={14} />}
              {order.advance_received ? "Received" : "Mark received"}
            </button>
            )}
          </div>
          <div className="text-[11px] text-[var(--text-muted)]">Expected: <span className="font-mono tabular-nums">{formatCr(advanceExpected)}</span></div>
          {order.advance_received && (
            <div className="mt-2">
              <div className="text-[11px] font-semibold text-[#455A64] mb-1.5">Amount received</div>
              <input
                type="number"
                min={0}
                step={0.01}
                defaultValue={order.advance_received_cr ?? advanceExpected}
                disabled={readOnly}
                onBlur={async (e) => {
                  const v = parseFloat(e.target.value);
                  if (Number.isNaN(v) || v === order.advance_received_cr) return;
                  await savePayment({ advance_received: true, advance_received_cr: v });
                }}
                data-testid={`order-advance-amount-${order.id}`}
                className="w-full h-9 bg-white border border-[#B8D3DF] rounded-xl px-3 text-[13px] font-mono outline-none focus:border-[#00A2D4]"
              />
              {order.advance_received_at && (
                <div className="text-[10px] text-[var(--text-dim)] mt-1">On {formatDate(order.advance_received_at)}</div>
              )}
            </div>
          )}
        </div>

        {/* Balance */}
        <div className={`border rounded-2xl p-3.5 ${order.balance_received ? "border-[#10B981]/40 bg-[#10B981]/5" : "border-[#D7E8F0] bg-[#F8FCFE]"}`}>
          <div className="flex items-center justify-between gap-2 mb-2">
            <div className="text-[11px] font-semibold text-[#455A64]">Balance</div>
            {!readOnly && (
            <button
              type="button"
              disabled={busy === "balance_received" || !order.advance_received}
              onClick={() => savePayment({ balance_received: !order.balance_received })}
              data-testid={`order-balance-toggle-${order.id}`}
              className="flex items-center gap-1.5 text-[11px] text-[var(--text-muted)] hover:text-[var(--saarv-blue)] disabled:opacity-50"
              title={!order.advance_received ? "Record advance first" : undefined}
            >
              {order.balance_received ? <CheckCircle2 size={14} className="text-[#10B981]" /> : <Circle size={14} />}
              {order.balance_received ? "Received" : "Mark received"}
            </button>
            )}
          </div>
          <div className="text-[11px] text-[var(--text-muted)]">Expected: <span className="font-mono tabular-nums">{formatCr(balanceExpected)}</span></div>
          {order.balance_received && (
            <div className="mt-2">
              <div className="text-[11px] font-semibold text-[#455A64] mb-1.5">Amount received</div>
              <input
                type="number"
                min={0}
                step={0.01}
                defaultValue={order.balance_received_cr ?? balanceExpected}
                disabled={readOnly}
                onBlur={async (e) => {
                  const v = parseFloat(e.target.value);
                  if (Number.isNaN(v) || v === order.balance_received_cr) return;
                  await savePayment({ balance_received: true, balance_received_cr: v });
                }}
                data-testid={`order-balance-amount-${order.id}`}
                className="w-full h-9 bg-white border border-[#B8D3DF] rounded-xl px-3 text-[13px] font-mono outline-none focus:border-[#00A2D4]"
              />
              {order.balance_received_at && (
                <div className="text-[10px] text-[var(--text-dim)] mt-1">On {formatDate(order.balance_received_at)}</div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function OrderFinanceNotes({ orderId, refreshKey = 0 }) {
  const [notes, setNotes] = useState([]);

  useEffect(() => {
    if (!orderId) return;
    api.get("/finance/notes", { params: { order_id: orderId } })
      .then((r) => setNotes(Array.isArray(r.data) ? r.data : []))
      .catch(() => setNotes([]));
  }, [orderId, refreshKey]);

  if (!notes.length) return null;

  return (
    <div className="border border-[var(--copper)]/25 rounded-md bg-[var(--copper)]/5 p-3" data-testid={`order-finance-notes-${orderId}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <Wallet size={13} className="text-[var(--copper)]" />
          <span className="text-[11px] uppercase tracking-widest text-[var(--text-dim)]">Finance commission releases</span>
        </div>
        <Link to="/finance" className="text-[10px] text-[var(--copper)] hover:underline">View all →</Link>
      </div>
      <div className="space-y-2">
        {notes.map((n) => (
          <div key={n.id} className="text-[12px] border border-black/10 rounded-md bg-white p-2.5">
            <div className="flex items-center gap-2 mb-1">
              <span className="text-[9px] uppercase tracking-wider text-[var(--text-muted)]">{n.payment_type}</span>
              <span className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded ${n.status === "released" ? "bg-[#10B981]/15 text-[#047857]" : "bg-[#F59E0B]/15 text-[#B45309]"}`}>
                {n.status}
              </span>
            </div>
            <div className="text-[var(--text)]">{n.message}</div>
            <div className="font-mono text-[11px] text-[var(--copper)] mt-1 tabular-nums">Release {formatCr(n.commission_due_cr)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

function OrderBookedModules({ order }) {
  const items = order.line_items || [];
  if (!items.length) {
    return (
      <div className="rounded-2xl border border-[#C9DCE6] bg-white p-4 text-[12px] text-[var(--text-muted)]" data-testid={`order-modules-${order.id}`}>
        Products: {(order.products || []).join(" · ") || "—"}
      </div>
    );
  }
  return (
    <div className="rounded-2xl border border-[#C9DCE6] bg-white p-4 space-y-2" data-testid={`order-modules-${order.id}`}>
      <div className="text-[11px] font-semibold text-[#455A64]">Booked modules ({MONEY_UNIT_SHORT})</div>
      <div className="divide-y divide-[#E1EBF0]">
        {items.map(item => (
          <div key={item.id} className="py-2 flex items-center justify-between gap-3 text-[12px]">
            <div className="min-w-0">
              <div className="font-medium text-[#243B53] truncate">{item.label || item.ergoniq_module}</div>
              <div className="text-[10px] text-[var(--text-muted)]">Qty {item.qty || 1}</div>
            </div>
            <div className="tabular-nums text-[#07539A] font-medium shrink-0">{formatLakh(item.price_cr)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Orders() {
  const { canWrite } = useAuth();
  const canEditOrders = canWrite("orders");
  const [searchParams] = useSearchParams();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [query, setQuery] = useState("");
  const [financeRefresh, setFinanceRefresh] = useState(0);
  const [filterOpen, setFilterOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState("all");
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [contractFilter, setContractFilter] = useState("all");

  useEffect(() => {
    async function fetchOrders() {
      setLoading(true);
      try {
        const { data } = await api.get("/orders");
        setOrders(data);
      } catch {
        toast.error("Could not load orders");
        setOrders([]);
      } finally {
        setLoading(false);
      }
    }
    fetchOrders();
  }, []);

  useEffect(() => {
    const q = searchParams.get("q");
    if (q) setQuery(q);
  }, [searchParams]);

  const filtered = orders.filter(o => {
    if (statusFilter !== "all" && o.status !== statusFilter) return false;
    if (paymentFilter !== "all" && o.payment_status !== paymentFilter) return false;
    if (contractFilter !== "all" && (o.contract_type || "single_year") !== contractFilter) return false;
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return [o.po_number, o.company?.name, o.status, o.payment_status, o.payment_terms, ...(o.products || [])].filter(Boolean).join(" ").toLowerCase().includes(q);
  });
  const total = filtered.reduce((s, o) => s + (o.value_cr || 0), 0);
  const activeFilterCount = [statusFilter !== "all", paymentFilter !== "all", contractFilter !== "all"].filter(Boolean).length;

  function clearFilters() {
    setStatusFilter("all");
    setPaymentFilter("all");
    setContractFilter("all");
  }

  function patchOrder(oid, patch) {
    setOrders((prev) => prev.map((x) => x.id === oid ? { ...x, ...patch } : x));
    setFinanceRefresh((k) => k + 1);
  }

  return (
    <div className="min-h-full bg-[#F7FAFC] pb-12" data-testid="orders-page">
      <section className="bg-[#07539A] px-5 md:px-9 pt-6 pb-16 shadow-[inset_0_-1px_0_rgba(255,255,255,0.12)]">
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-5">
          <div>
            <h1 className="font-display text-3xl md:text-4xl tracking-tight text-[#FFFFFF]">Orders</h1>
            <p className="text-[13px] text-white/75 mt-1">Confirmed business from PO and payment terms through delivery and realised revenue.</p>
          </div>
          <div className="text-left md:text-right shrink-0">
            <div className="text-[10px] uppercase tracking-widest text-white/60">Total booked</div>
            <div className="font-display text-2xl md:text-3xl tabular-nums !text-white mt-1">{formatCr(total)}</div>
          </div>
        </div>
      </section>

      <div className="px-4 md:px-8 -mt-7 relative z-10 space-y-5">
        <PageToolbar
          query={query}
          onQueryChange={setQuery}
          placeholder="Search orders by PO number, customer, product, or payment status…"
          entityLabel="orders"
          count={filtered.length}
          variant="universe"
          right={(
            <div className="relative ml-auto">
              <button
                type="button"
                onClick={() => setFilterOpen(open => !open)}
                data-testid="orders-filter-btn"
                className={`h-9 px-3 rounded-lg border text-[12px] font-medium flex items-center gap-2 shadow-[0_4px_12px_rgba(7,83,154,0.06)] transition-colors ${filterOpen || activeFilterCount ? "border-[#07539A] bg-[#E3F2F8] text-[#07539A]" : "border-[#BFD6E1] bg-white text-[#243B53] hover:border-[#07539A]"}`}
              >
                <Filter size={14} /> Filter
                {activeFilterCount > 0 && <span className="w-5 h-5 rounded-full bg-[#07539A] !text-white text-[10px] flex items-center justify-center">{activeFilterCount}</span>}
              </button>

              {filterOpen && (
                <div className="absolute right-0 top-11 z-30 w-[min(92vw,34rem)] rounded-2xl border border-[#C9DCE6] bg-white shadow-[0_20px_50px_rgba(7,83,154,0.20)] p-4" data-testid="orders-filter-panel">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <div className="font-display text-lg text-[#07539A]">Filter orders</div>
                      <div className="text-[10px] text-[#78909C] mt-0.5">Focus the order book by fulfilment, payment, and contract status.</div>
                    </div>
                    <button type="button" onClick={() => setFilterOpen(false)} aria-label="Close filters" className="w-8 h-8 rounded-full hover:bg-[#EDF7FB] text-[#607D8B] flex items-center justify-center"><X size={14} /></button>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <OrderFilterSelect label="Order status" value={statusFilter} onChange={setStatusFilter} testId="orders-status-filter" options={[
                      { key: "all", label: "All statuses" },
                      ...ORDER_STATUSES.map(value => ({ key: value, label: orderLabel(value) })),
                    ]} />
                    <OrderFilterSelect label="Payment" value={paymentFilter} onChange={setPaymentFilter} testId="orders-payment-filter" options={[
                      { key: "all", label: "All payments" },
                      ...Object.entries(PAYMENT_STATUS_LABEL).map(([key, label]) => ({ key, label })),
                    ]} />
                    <OrderFilterSelect label="Contract" value={contractFilter} onChange={setContractFilter} testId="orders-contract-filter" options={[
                      { key: "all", label: "All contracts" },
                      { key: "single_year", label: "Single-year" },
                      { key: "multi_year", label: "Multi-year" },
                    ]} />
                  </div>
                  <div className="mt-4 pt-3 border-t border-[#E1EBF0] flex items-center justify-between">
                    <button type="button" onClick={clearFilters} className="text-[11px] text-[#607D8B] hover:text-[#07539A]">Clear all</button>
                    <button type="button" onClick={() => setFilterOpen(false)} className="h-9 px-4 rounded-xl bg-[#07539A] !text-white text-[12px] font-medium">Show {filtered.length} orders</button>
                  </div>
                </div>
              )}
            </div>
          )}
        />

        {loading ? (
          <div className="bg-white border border-[#C9DCE6] rounded-[22px] shadow-[0_10px_28px_rgba(7,83,154,0.08)] text-[#78909C] p-10 text-center">Loading orders…</div>
        ) : filtered.length === 0 ? (
          <div className="text-center p-10 border border-dashed border-[#B8D3DF] bg-white rounded-[22px]">
            <span className="w-11 h-11 rounded-xl bg-[#E7F5FB] text-[#07539A] flex items-center justify-center mx-auto mb-3"><Package size={21} /></span>
            <div className="text-[13px] text-[#78909C]">{orders.length === 0 ? "No orders yet." : "No orders match your search."}</div>
          </div>
        ) : (
        <div className="border border-[#C9DCE6] rounded-[22px] overflow-x-auto bg-white shadow-[0_10px_28px_rgba(7,83,154,0.08)]" data-testid="orders-table-scroll">
          <div className="grid min-w-[1080px] grid-cols-[1.1fr_1.35fr_1fr_1.15fr_0.7fr_1fr_90px_90px_20px] gap-3 px-4 py-3 text-[10px] font-semibold text-[#07539A] border-b border-[#D7E8F0] bg-[#F3FAFD]">
            <div>PO number</div>
            <div>Company</div>
            <div>Contract</div>
            <div>Products</div>
            <div className="text-center">Value</div>
            <div>Payment</div>
            <div className="text-center">Expected</div>
            <div className="text-center">Status</div>
            <div></div>
          </div>
          <div className="min-w-[1080px] divide-y divide-[#E1EBF0]">
            {filtered.map(o => {
              const isExp = expanded === o.id;
              return (
              <div key={o.id} data-testid={`order-${o.id}`}>
                <button onClick={() => setExpanded(isExp ? null : o.id)} className="w-full text-left" data-testid={`order-expand-${o.id}`}>
                <div className="grid grid-cols-[1.1fr_1.35fr_1fr_1.15fr_0.7fr_1fr_90px_90px_20px] gap-3 px-4 py-4 items-center hover:bg-[#F8FCFE] transition-colors">
                  <div className="text-[11px] font-medium text-[#455A64] truncate">{o.po_number}</div>
                  <div className="text-[12px] font-medium text-[#455A64] truncate">{o.company?.name}</div>
                  <div className="text-[11px] text-[#607D8B] truncate flex gap-2 items-center">
                    <ContractBadge order={o} />
                  </div>
                  <div className="text-[11px] text-[#607D8B] truncate">{(o.products || []).join(" · ") || "—"}</div>
                  <div className="text-center text-[12px] font-medium text-[#455A64] tabular-nums">{formatCr(o.value_cr)}</div>
                  <div><PaymentBadge status={o.payment_status || "awaiting_advance"} /></div>
                  <div className="text-center text-[11px] text-[#607D8B]">{formatDate(o.expected_delivery)}</div>
                  <div className="text-center">
                    <span className="text-[9px] font-semibold px-2.5 py-1 rounded-full border inline-block" style={{
                      color: STATUS_COLOR[o.status], borderColor: `${STATUS_COLOR[o.status]}40`, background: `${STATUS_COLOR[o.status]}10`
                    }}>{o.status.replace("_", " ")}</span>
                  </div>
                  <div className="text-[#9AAAB3]">{isExp ? <ChevronDown size={14} /> : <ChevronRight size={14} />}</div>
                </div>
                </button>
                {isExp && (
                  <div className="sticky left-0 w-[calc(100vw-2rem)] max-w-[calc(100vw-2rem)] md:static md:w-auto md:max-w-none px-3 md:px-5 pb-5 pt-4 bg-[#EAF6FB] border-t border-[#D7E8F0] space-y-4" data-testid={`order-details-${o.id}`}>
                    <OrderBookedModules order={o} />
                    <div className="rounded-[20px] overflow-hidden bg-white border border-[#C9DCE6] shadow-[0_7px_20px_rgba(7,83,154,0.05)] [&>[data-testid='partner-assignment']]:border-0">
                      <PartnerAssignmentPanel entityType="company" entityId={o.company_id} accent="navy" />
                    </div>
                    <OrderPaymentPanel order={o} onPatch={patchOrder} readOnly={!canEditOrders} />
                    <OrderContractPanel order={o} onPatch={patchOrder} readOnly={!canEditOrders} />
                    <OrderFinanceNotes orderId={o.id} refreshKey={financeRefresh} />
                    <div className="grid md:grid-cols-2 gap-3">
                      <div className="rounded-2xl border border-[#C9DCE6] bg-white p-3 shadow-[0_4px_14px_rgba(7,83,154,0.04)]">
                        <div className="text-[11px] font-semibold text-[#455A64] mb-1.5">Status</div>
                        <select
                          value={o.status}
                          disabled={!canEditOrders}
                          onChange={async (e) => {
                            const status = e.target.value;
                            try {
                              const { data } = await api.patch(`/orders/${o.id}`, { status });
                              patchOrder(o.id, data);
                              toast.success("Order status updated");
                            } catch { toast.error("Update failed"); }
                          }}
                          data-testid={`order-status-${o.id}`}
                          className="w-full h-10 bg-white border border-[#B8D3DF] rounded-xl px-3 text-[13px] outline-none focus:border-[#00A2D4]"
                        >
                          {ORDER_STATUSES.map((s) => (
                            <option key={s} value={s}>{s.replace("_", " ")}</option>
                          ))}
                        </select>
                      </div>
                      <EditableField
                        label="PO number"
                        value={o.po_number}
                        testId={`order-po-${o.id}`}
                        readOnly={!canEditOrders}
                        className="!rounded-2xl !border-[#C9DCE6] bg-white !p-3 shadow-[0_4px_14px_rgba(7,83,154,0.04)]"
                        labelClassName="text-[11px] font-semibold text-[#455A64]"
                        inputClassName="text-[#243B53]"
                        onSave={async (v) => {
                          const { data } = await api.patch(`/orders/${o.id}`, { po_number: v.trim() });
                          patchOrder(o.id, data);
                          toast.success("PO number updated");
                        }}
                      />
                    </div>
                    <EditableField
                      label="Notes"
                      value={o.notes}
                      testId={`order-notes-${o.id}`}
                      multiline
                      readOnly={!canEditOrders}
                      className="!rounded-2xl !border-[#C9DCE6] bg-white !p-3 shadow-[0_4px_14px_rgba(7,83,154,0.04)]"
                      labelClassName="text-[11px] font-semibold text-[#455A64]"
                      inputClassName="text-[#243B53]"
                      onSave={async (v) => {
                        const { data } = await api.patch(`/orders/${o.id}`, { notes: v.trim() });
                        patchOrder(o.id, data);
                        toast.success("Notes updated");
                      }}
                    />
                    <MeetingLogInline entityType="order" entityId={o.id} accent="navy" />
                  </div>
                )}
              </div>
            );})}
          </div>
        </div>
        )}
      </div>
    </div>
  );
}
